import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Tabs,
  Tab,
  Typography,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  GlobalStyles,
  Collapse,
  Paper,
} from '@mui/material';
import {
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Article as TemplateIcon,
  AutoAwesome as AutoAwesomeIcon,
  Chat as ChatIcon,
  AttachFile as AttachFileIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useSnackbar } from 'notistack';
import { useQuery, useQueryClient } from 'react-query';
import { soapNotesService } from '../../services/soapNotesService';
import { useAuth } from '../../auth/useAuth';
import { executeWrite, executeUpdate, skipWhenOffline, OfflineWriteBlockedError, withOfflineFallback, readSoapTemplates, OfflineNotice, useIsOffline } from '../../offline';
import {
  SoapNote,
  SoapTemplate,
  CreateSoapNoteDto,
  SoapNoteStatus,
  SoapSubjective,
  SoapObjective,
  SoapAssessment,
  SoapPlan,
  MedicationToAdd,
  ProcedureToAdd,
} from '../../types/soap';
import SubjectiveSection from './SubjectiveSection';
import ObjectiveSection from './ObjectiveSection';
import AssessmentSection from './AssessmentSection';
import PlanSection from './PlanSection';
import VoiceSoapRecorder from '../VoiceSoapRecorder';
import MedicationConfirmationDialog from './MedicationConfirmationDialog';
import ProcedureConfirmationDialog from './ProcedureConfirmationDialog';
import VaccinationDialog from '../Vaccination/VaccinationDialog';
import ExistingVaccinationDialog from '../Vaccination/ExistingVaccinationDialog';
import { splitPlanVaccines } from './planVaccines';
import { detectVaccines } from '../Vaccination/detectVaccines';
import { serviceCatalogService, ServiceCatalogItem } from '../../services/serviceCatalogService';
import { VaccinationService, toCreateVaccinationDto } from '../../services/vaccinationService';
import { useClinicTimezone } from '../../hooks/useClinicTimezone';
import { formatAnchoredDayOrInstant, formatInstantInZone } from '../../utils/dateUtils';
import TemplateFormRenderer, { TemplateFieldControl } from './TemplateFormRenderer';
import AIReviewPanel, { AIFieldSuggestion } from './AIReviewPanel';
import FileDropZone, { UploadedAttachment } from './FileDropZone';
import api from '../../utils/api';
import { useAnalytics } from '../../hooks/useAnalytics';
import { createDraftCoordinator } from './draftCoordinator';
import { patientFilesQueryKey } from '../PatientFiles/usePatientFiles';
import { normalizeList } from '../PatientFiles/patientFiles';

interface SoapNoteDialogProps {
  open: boolean;
  onClose: () => void;
  horseId: string;
  horseName?: string;
  horseBreed?: string;
  horseAge?: number;
  existingNote?: SoapNote;
  onSave?: (note: SoapNote) => void;
  /** Barn-visit workflow: link new notes to the visit's appointment. */
  appointmentId?: string;
  /** Default the note date (e.g. the visit date) instead of "now". */
  initialDate?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`soap-tabpanel-${index}`}
      aria-labelledby={`soap-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

/**
 * Match a template field to structured voice AI data by key/label pattern.
 * Returns the matched value or undefined if no match found.
 */
function matchVoiceDataToField(
  field: TemplateFieldControl,
  sectionData: any,
  allData: {
    subjective?: SoapSubjective;
    objective?: SoapObjective;
    assessment?: SoapAssessment;
    plan?: SoapPlan;
  },
): any {
  const k = (field.key || '').toLowerCase().replace(/[-\s]/g, '_');
  const label = (field.label || '').toLowerCase();

  // --- Objective: Vitals ---
  const vitals = allData.objective?.vitals;
  if (vitals) {
    if (/temp/.test(k) || /temperature/.test(label)) return vitals.temperature?.value;
    if (/heart_rate|pulse|hr/.test(k) || /heart rate|pulse/.test(label)) return vitals.pulse?.value;
    if (/resp|rr/.test(k) || /respir/.test(label)) return vitals.respiration?.value;
    if (/weight|bw|body_weight/.test(k) || /weight/.test(label)) return vitals.weight?.value;
  }

  // --- Objective: Physical Exam ---
  const pe = allData.objective?.physicalExam;
  if (pe) {
    if (/physical_exam|pe_findings/.test(k) || /physical exam/.test(label)) {
      return pe.aiFindings || Object.entries(pe)
        .filter(([key, val]) => key !== 'aiFindings' && Array.isArray(val) && val.length)
        .map(([key, val]) => `${key}: ${(val as string[]).join(', ')}`)
        .join('\n') || undefined;
    }
    // Match specific PE systems
    const peKeys = ['general', 'cardiovascular', 'respiratory', 'gastrointestinal', 'musculoskeletal', 'integumentary', 'neurological'] as const;
    for (const sys of peKeys) {
      if (k.includes(sys) || label.includes(sys)) {
        const arr = pe[sys];
        return arr?.length ? arr.join(', ') : undefined;
      }
    }
  }

  // --- Subjective ---
  if (/chief_complaint|complaint|presenting/.test(k) || /chief complaint|presenting complaint|reason/.test(label)) {
    return allData.subjective?.notes || allData.subjective?.aiSummary;
  }
  if (/history|hx/.test(k) || /history/.test(label)) {
    return allData.subjective?.notes || allData.subjective?.aiSummary;
  }

  // --- Assessment ---
  if (/diagnosis|dx|primary_diagnosis/.test(k) || /diagnosis/.test(label)) return allData.assessment?.primaryDiagnosis;
  if (/differential/.test(k) || /differential/.test(label)) {
    const diffs = allData.assessment?.differentials;
    return diffs?.length ? diffs.join(', ') : undefined;
  }
  if (/prognosis/.test(k) || /prognosis/.test(label)) return allData.assessment?.prognosis;

  // --- Plan ---
  if (/follow.?up|recheck/.test(k) || /follow.?up|recheck/.test(label)) {
    return (allData.plan as any)?.followUpInstructions || (allData.plan as any)?.planNotes;
  }
  if (/client.?education|instructions/.test(k) || /client education|discharge/.test(label)) {
    return (allData.plan as any)?.clientEducation;
  }
  if (/diagnostic|labs_ordered/.test(k) || /diagnostic/.test(label)) {
    const diags = allData.plan?.diagnosticsOrdered;
    return diags?.length ? diags.join(', ') : undefined;
  }

  // --- Fallback for textarea/text: use aiSummary for the matching section ---
  if (field.type === 'textarea' || field.type === 'text') {
    return sectionData?.aiSummary || sectionData?.notes || undefined;
  }

  return undefined;
}

/**
 * Flatten a template schema by extracting children from group fields.
 * Returns all leaf-level fields (non-group) with their parent's section inherited.
 * Used for voice→template mapping and template value restoration where we need
 * to iterate ALL individual fields, not just top-level ones.
 */
function flattenSchema(schema: TemplateFieldControl[]): TemplateFieldControl[] {
  const result: TemplateFieldControl[] = [];
  schema.forEach(field => {
    if (field.type === 'group' && field.children) {
      field.children.forEach(child => {
        // Children inherit the parent's section if they don't have one
        result.push({ ...child, section: child.section || field.section });
      });
    } else if (field.type !== 'treatment_plan') {
      // The Plan block writes the note's plan arrays itself; it has no template value to map.
      result.push(field);
    }
  });
  return result;
}

/** The default template: a built-in with no fields of its own, i.e. the standard four-section note (General Exam). */
const isDefaultLayout = (t: SoapTemplate): boolean =>
  !!t.isSystemDefault && !(Array.isArray(t.schema) && t.schema.length > 0);

/**
 * Build a readable aiSummary string from template field values for a given section.
 * Used as fallback when no AI-generated aiSummary exists.
 */
function formatTemplateValue(val: any): string {
  if (Array.isArray(val)) {
    if (val.length === 0) return '';
    if (typeof val[0] === 'object' && val[0]?.fileName) return `${val.length} file(s) attached`;
    return val.filter(v => typeof v === 'string').join(', ');
  }
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (typeof val === 'object' && val !== null) return '(recorded)';
  return String(val);
}

function buildSummaryFromTemplateValues(
  section: string,
  schema: TemplateFieldControl[],
  values: Record<string, any>,
): string {
  const lines: string[] = [];
  schema.forEach(field => {
    if (field.section !== section) return;
    const val = values[field.key];
    if (val === undefined || val === '' || val === null) return;
    if (field.type === 'group' && field.children) {
      const groupVal = values[field.key] as Record<string, any> | undefined;
      if (!groupVal) return;
      field.children.forEach(child => {
        const childVal = groupVal[child.key];
        if (childVal !== undefined && childVal !== '' && childVal !== null) {
          lines.push(`${child.label}: ${formatTemplateValue(childVal)}`);
        }
      });
    } else {
      lines.push(`${field.label}: ${formatTemplateValue(val)}`);
    }
  });
  return lines.join('\n');
}

// Sentinel fieldKey for files uploaded via the universal "Attachments" section.
const NOTE_FILES_FIELD_KEY = '__note_files__';

// Fallback schema used for AI document-extraction when NO template is selected,
// so the "read a document into the note" sparkle works on every note (not just
// template ones). The four keys map 1:1 to the legacy SOAP sections.
const GENERIC_SOAP_SCHEMA: TemplateFieldControl[] = [
  { key: 'subjective', label: 'Subjective', section: 'subjective', type: 'textarea' },
  { key: 'objective', label: 'Objective', section: 'objective', type: 'textarea' },
  { key: 'assessment', label: 'Assessment', section: 'assessment', type: 'textarea' },
  { key: 'plan', label: 'Plan', section: 'plan', type: 'textarea' },
];

const SoapNoteDialog: React.FC<SoapNoteDialogProps> = ({
  open,
  onClose,
  horseId,
  horseName,
  horseBreed,
  horseAge,
  existingNote,
  onSave,
  appointmentId,
  initialDate,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  // B13: stamp the author on the OPTIMISTIC row so the chart's "Dr. X" shows
  // immediately for an offline draft (the server stamps the authoritative
  // provider from the JWT on sync; SOAP is the only chart card rendering one).
  const { user: authUser } = useAuth();
  const { trackFeatureUsed } = useAnalytics();
  // Reactive — swaps the voice recorder for a notice the moment connectivity
  // is observed down (and back). False whenever the offline flag is off.
  const isOffline = useIsOffline();
  const autosaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);
  // Universal "Associated Files" attachments for this note (sentinel fieldKey).
  const [noteAttachments, setNoteAttachments] = useState<UploadedAttachment[]>([]);

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState<SoapTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [isDirty, setIsDirty] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [subscriptionTier, setSubscriptionTier] = useState<string | null>(null);
  const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
  const [showMedicationDialog, setShowMedicationDialog] = useState(false);
  // Vaccines in the plan become vaccination records (spec §2), asked inline or on Complete.
  // A pending completion waits on the prompt: Save or Skip carry it on, Back (or Escape) abandons it.
  const [vaccinePrompt, setVaccinePrompt] = useState<{ completion?: { noteId: string; plan: SoapPlan | null } } | null>(null);
  const [recordedVaccines, setRecordedVaccines] = useState<Set<string>>(() => new Set());
  // Answers for plan vaccines already on the chart that day: 'same' (that dose, record nothing) or 'new'.
  const [doseDecisions, setDoseDecisions] = useState<Record<string, 'same' | 'new'>>({});
  // Vaccines recorded from the prompt this time (catalog names), read when the dialog closes after a save.
  const savedFromPromptRef = useRef<Set<string> | null>(null);
  // The vaccines that become vaccination records at this completion, so they are not offered as medications too.
  const [completionVaccines, setCompletionVaccines] = useState<Set<string>>(() => new Set());
  const [showProcedureDialog, setShowProcedureDialog] = useState(false);
  const [pendingNoteId, setPendingNoteId] = useState<string | null>(null);
  const [pendingMedications, setPendingMedications] = useState<MedicationToAdd[]>([]);
  const [pendingMergedPlan, setPendingMergedPlan] = useState<SoapPlan | null>(null);

  // Template schema mode
  const [templateSchema, setTemplateSchema] = useState<TemplateFieldControl[] | null>(null);
  const [templateValues, setTemplateValues] = useState<Record<string, any>>({});
  // The fields the AI may fill: the Plan block is filled through the note's plan, never as a field.
  const aiTemplateSchema = React.useMemo(
    () => templateSchema?.filter((f) => f.type !== 'treatment_plan') ?? null,
    [templateSchema],
  );
  const [aiSuggestions, setAiSuggestions] = useState<AIFieldSuggestion[] | null>(null);
  const [aiDocMeta, setAiDocMeta] = useState<{ name?: string; summary?: string; documentType?: string; warnings?: string[] } | null>(null);

  const [soapData, setSoapData] = useState<CreateSoapNoteDto>({
    id: existingNote?.id,
    horseId,
    date: existingNote?.date || initialDate || new Date().toISOString(),
    subjective: existingNote?.subjective || {},
    objective: existingNote?.objective || {},
    assessment: existingNote?.assessment || {},
    plan: existingNote?.plan || {},
    status: existingNote?.status || SoapNoteStatus.DRAFT,
    ...(appointmentId ? { appointmentId } : {}),
  });

  // Vaccines among the plan's treatments and medications, by the clinic's vaccination catalog (incl. aliases).
  const { data: vaccineCatalog = [] } = useQuery<ServiceCatalogItem[]>(
    ['service-catalog', 'vaccination'],
    () => serviceCatalogService.getAll({ category: 'vaccination' }),
    { staleTime: 5 * 60 * 1000, retry: false },
  );
  const { data: medicationCatalog = [] } = useQuery<ServiceCatalogItem[]>(
    ['service-catalog', 'medication'],
    () => serviceCatalogService.getAll({ category: 'medication' }),
    { staleTime: 5 * 60 * 1000, retry: false },
  );
  // The Plan's treatment-name suggestions, vaccines first: picking one makes the vaccine match exact.
  // The list keys rows by label and catalog names are not unique, so a repeated name is listed once.
  const treatmentOptions = React.useMemo(() => {
    const options = [
      ...vaccineCatalog.filter((c) => c.isActive).map((c) => ({ label: c.name, group: 'Vaccines' })),
      ...medicationCatalog.filter((c) => c.isActive).map((c) => ({ label: c.name, group: 'Medications' })),
    ];
    return options.filter((o, i) => options.findIndex((p) => p.label === o.label) === i);
  }, [vaccineCatalog, medicationCatalog]);
  const detectedVaccines = React.useMemo(
    () =>
      detectVaccines(
        [
          ...(Array.isArray(soapData.plan?.treatments) ? soapData.plan!.treatments! : []),
          ...(Array.isArray(soapData.plan?.medicationsPrescribed) ? soapData.plan!.medicationsPrescribed! : []),
        ],
        vaccineCatalog,
      ),
    [soapData.plan, vaccineCatalog],
  );
  const vaccineEntryNames = React.useMemo(
    () => new Set(detectedVaccines.map((v) => v.enteredName.trim().toLowerCase())),
    [detectedVaccines],
  );
  // A vaccine already on the chart for this note's day (recorded from this note before it was reopened, or
  // from the patient chart) asks before a second record is made: a second one would draw the lot twice.
  const clinicTz = useClinicTimezone();
  const { data: horseVaccinations = [], isFetched: chartRead, isError: chartReadFailed } = useQuery(
    ['vaccinations', horseId, 'soap-note'],
    () => VaccinationService.getAll({ horseId }),
    { enabled: open && detectedVaccines.length > 0, staleTime: 30 * 1000, retry: false },
  );
  // The prompts wait for that read, so a duplicate is never offered in the moment before it lands.
  const chartSettled = detectedVaccines.length === 0 || chartRead || chartReadFailed;
  // The note's date is a moment, never a day: 8 PM Eastern is exactly T00:00:00.000Z, which a shape rule reads as tomorrow.
  const noteDay = formatInstantInZone(soapData.date, clinicTz, 'yyyy-MM-dd');
  const sameDayDoses = React.useMemo(() => {
    const byName = new Map<string, any[]>();
    for (const v of horseVaccinations) {
      if (v.isArchived || formatAnchoredDayOrInstant(v.dateAdministered, clinicTz, 'yyyy-MM-dd') !== noteDay) continue;
      const name = (detectVaccines([{ name: v.vaccineName }], vaccineCatalog)[0]?.catalogItem.name ?? v.vaccineName).trim().toLowerCase();
      byName.set(name, [...(byName.get(name) ?? []), v]);
    }
    return byName;
  }, [horseVaccinations, clinicTz, noteDay, vaccineCatalog]);
  const dosesOnChart = (name: string) => sameDayDoses.get(name.trim().toLowerCase()) ?? [];
  const planVaccineNames = detectedVaccines.map((v) => v.catalogItem.name);
  const { pending: planVaccines, toAsk, toRecord: vaccinesToRecord } = splitPlanVaccines(
    planVaccineNames,
    recordedVaccines,
    doseDecisions,
    (name) => dosesOnChart(name).length > 0,
  );
  // Recorded from this note or confirmed as the dose on the chart: a vaccination record, not a medication.
  const settledVaccines = (extra: Iterable<string> = []) =>
    new Set([
      ...Array.from(recordedVaccines),
      ...Object.keys(doseDecisions).filter((name) => doseDecisions[name] === 'same' && dosesOnChart(name).length > 0),
      ...Array.from(extra),
    ]);
  // Every plan row of a settled vaccine, whatever its spelling, stays off the medication list: a vaccine is a
  // vaccination record or a medication, never both (that would take its stock twice).
  const isRecordedVaccineRow = (row: { name: string }, recorded: Set<string>) => {
    const hit = detectVaccines([row], vaccineCatalog)[0];
    return !!hit && recorded.has(hit.catalogItem.name);
  };

  // Reset form data when existingNote changes
  useEffect(() => {
    setSoapData({
      id: existingNote?.id,
      horseId,
      date: existingNote?.date || initialDate || new Date().toISOString(),
      subjective: existingNote?.subjective || {},
      objective: existingNote?.objective || {},
      assessment: existingNote?.assessment || {},
      plan: existingNote?.plan || {},
      status: existingNote?.status || SoapNoteStatus.DRAFT,
      ...(appointmentId ? { appointmentId } : {}),
    });
    setIsDirty(false);
    setLastSaved(null);

    // Restore template state if this note used a template (drafts and completed notes)
    if (existingNote?.templateUsed) {
      const restoreTemplate = async () => {
        try {
          const allTemplates = await withOfflineFallback(
            () => soapNotesService.getTemplates(),
            () => readSoapTemplates() as Promise<any>,
          )();
          // Notes name their template; on a tie the clinic's own beats a built-in (isDefault) one.
          const named = allTemplates.filter((t: any) => t.name === existingNote.templateUsed);
          const match = named.find((t: any) => !t.isDefault) ?? named[0];
          if (match?.schema && Array.isArray(match.schema) && match.schema.length > 0) {
            const schema = match.schema as TemplateFieldControl[];
            setTemplateSchema(schema);
            setSelectedTemplateId(match.id);

            // Extract template field values from saved section data
            // Group children must be nested: restored[groupKey] = { childKey: value }
            const restored: Record<string, any> = {};
            schema.forEach((f: TemplateFieldControl) => {
              if (f.type === 'group' && f.children) {
                const groupValues: Record<string, any> = {};
                f.children.forEach((child: TemplateFieldControl) => {
                  const sectionData = (existingNote as any)[child.section || f.section];
                  if (sectionData?.[child.key] !== undefined) {
                    groupValues[child.key] = sectionData[child.key];
                  }
                });
                if (Object.keys(groupValues).length > 0) {
                  restored[f.key] = groupValues;
                }
              } else if (f.type !== 'treatment_plan') {
                const sectionData = (existingNote as any)[f.section];
                if (sectionData?.[f.key] !== undefined) {
                  restored[f.key] = sectionData[f.key];
                }
              }
            });
            setTemplateValues(restored);
          } else {
            setSelectedTemplateId('');
            setTemplateSchema(null);
            setTemplateValues({});
          }
        } catch (error) {
          console.error('Failed to restore template:', error);
          setSelectedTemplateId('');
          setTemplateSchema(null);
          setTemplateValues({});
        }
      };
      restoreTemplate();
    } else {
      setSelectedTemplateId('');
      setTemplateSchema(null);
      setTemplateValues({});
    }
  }, [existingNote, horseId]);

  // Auto-draft bookkeeping (used by the new-note guard, the load effect, and
  // ensureNoteId below):
  //  - draftCoordinator dedupes concurrent draft creation (several files dropped
  //    at once share one POST) and discards a draft whose note changed mid-flight
  //    (PHI cross-attach guard) — see ./draftCoordinator, unit-tested there.
  //  - openEpochRef is the epoch the coordinator compares: bumped on reopen.
  //  - skipNextLoadRef skips the just-created note's initial GET so a racing
  //    empty response can't clobber the optimistic upload row.
  const draftCoordinator = useRef(createDraftCoordinator()).current;
  const openEpochRef = useRef(0);
  const skipNextLoadRef = useRef<string | null>(null);

  // The dialog instance is always mounted and keyed `new-soap-note` for EVERY
  // new note, so opening a second new note after saving a first one does NOT
  // remount, and the existingNote-keyed reset effect above doesn't re-run
  // (existingNote stays undefined). Without this, the new note would inherit the
  // previous note's id/content/attachments — editing/overwriting the wrong note
  // and leaking its files (PHI). Reset whenever we (re)open for a new note that
  // carries over state (a saved id OR typed-but-unsaved content).
  // Scoped to the new-note case only (existingNote falsy).
  useEffect(() => {
    // Per-note state, and this instance outlives the note (new and saved notes alike). A completion with
    // nothing to confirm closes the dialog with `loading` still set, which left the next note's buttons disabled.
    if (open) {
      setRecordedVaccines(new Set());
      setDoseDecisions({});
      setLoading(false);
    }
    if (open && !existingNote) {
      // Bump the epoch + drop any in-flight draft from a PREVIOUS new note. Its
      // round-trip may not have resolved yet (so soapData.id can still be falsy
      // here); the coordinator discards it on resolution when the epoch moved.
      openEpochRef.current += 1;
      draftCoordinator.reset();
      if (soapData.id || isDirty) {
        setSoapData({
          id: undefined,
          horseId,
          date: initialDate || new Date().toISOString(),
          subjective: {},
          objective: {},
          assessment: {},
          plan: {},
          status: SoapNoteStatus.DRAFT,
          ...(appointmentId ? { appointmentId } : {}),
        });
        setSelectedTemplateId('');
        setTemplateSchema(null);
        setTemplateValues({});
        setNoteAttachments([]);
        setIsDirty(false);
        setLastSaved(null);
      } else {
        // The date is on screen: a blank note reopened later starts at this opening, not the last one.
        setSoapData((prev) => ({ ...prev, date: initialDate || new Date().toISOString() }));
      }
    }
    // Only react to open transitions; existingNote/soapData.id/isDirty read fresh.
  }, [open]);

  // Load templates and subscription status on mount
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const templatesData = await withOfflineFallback(
          () => soapNotesService.getTemplates(),
          () => readSoapTemplates() as Promise<any>,
        )();
        setTemplates(templatesData);
      } catch (error) {
        console.error('Failed to load templates:', error);
      }
    };

    const checkSubscription = async () => {
      try {
        const response = await api.get('/subscription/status');
        const { status, tier } = response.data;
        setSubscriptionTier(tier);
        setIsSubscribed(status === 'ACTIVE' || status === 'TRIALING');
      } catch (error) {
        console.error('Failed to check subscription:', error);
        setIsSubscribed(false);
      }
    };

    if (open) {
      if (!existingNote) {
        loadTemplates();
      }
      checkSubscription();
    }
  }, [open, existingNote]);

  // Autosave functionality
  const autosave = useCallback(async () => {
    if (!isDirty || !soapData.id) return;

    setSaving(true);
    try {
      const mergedData = mergeTemplateValuesToSoapData();
      // Autosave only ever runs for a note that already has an id (gate above),
      // so this is always an update — offline it queues, online it upserts as before.
      const updatedNote: any = await executeUpdate({
        entityType: 'soapNote',
        table: 'soapNotes',
        entityId: soapData.id as string,
        payload: mergedData,
        networkFn: () => soapNotesService.upsert(mergedData),
      });
      setSoapData({ ...soapData, id: updatedNote.id });
      setLastSaved(new Date());
      setIsDirty(false);
    } catch (error) {
      console.error('Autosave failed:', error);
      // isDirty stays true (the setIsDirty(false) above is unreached), so the note
      // still reflects unsaved state — but a silent durability-gate block would let
      // the vet believe autosave persisted their PHI. Surface it like the manual
      // save paths do (the other write errors stay console-only, as before).
      if (error instanceof OfflineWriteBlockedError) {
        enqueueSnackbar(error.message, { variant: 'error' });
      }
    } finally {
      setSaving(false);
    }
  }, [soapData, isDirty, templateSchema, templateValues]);

  // Setup autosave timer
  useEffect(() => {
    if (isDirty) {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
      autosaveTimerRef.current = setTimeout(autosave, 5000); // Autosave after 5 seconds
    }

    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
    };
  }, [isDirty, autosave]);

  // Load the universal "Associated Files" attachments for an existing/saved
  // note. A brand-new note has no id yet (no parent to query) — clear any stale
  // list from a previously-open note. Online-only (blobs aren't replicated).
  useEffect(() => {
    if (!open) return;
    if (!soapData.id) {
      setNoteAttachments([]);
      return;
    }
    if (isOffline) return;
    // Just auto-created this draft locally — its only file is the one being
    // uploaded right now (set via the dropzone's onChange); skip the GET so a
    // racing empty response can't clobber the optimistic row. Real reopens of
    // the note (id unchanged but not just-created) still load normally.
    if (skipNextLoadRef.current === soapData.id) {
      skipNextLoadRef.current = null;
      return;
    }
    let cancelled = false;
    api
      .get('/attachments', {
        // ALL files for this note (any fieldKey) — the unified Attachments
        // section is the single place files live, including any uploaded to a
        // template's former file_drop field.
        params: {
          parentType: 'soap_note',
          parentId: soapData.id,
        },
      })
      .then((r) => {
        if (cancelled) return;
        setNoteAttachments(normalizeList<UploadedAttachment>(r.data));
      })
      .catch(() => {
        // Leave the current list as-is on a transient failure. Existing notes
        // remount per note id (fresh []), and new notes are cleared by the
        // guard effect above, so a stale cross-note list can't survive here.
      });
    return () => {
      cancelled = true;
    };
  }, [open, soapData.id, isOffline]);

  // Handle template selection
  const handleTemplateSelect = async (templateId: string) => {
    if (!templateId) {
      setTemplateSchema(null);
      setTemplateValues({});
      return;
    }

    setLoading(true);
    try {
      // Same fallback as the template loaders above — the dropdown is populated
      // offline from the replica, so applying must work offline too. Replica
      // template rows carry the full .schema (the restore-on-open path at the
      // top of this file depends on it).
      const template = await withOfflineFallback(
        () => soapNotesService.getTemplate(templateId),
        async () => {
          const match = (await readSoapTemplates()).find((t: any) => t.id === templateId);
          if (!match) throw new Error('Template not found in offline replica');
          return match as any;
        },
      )();
      setSelectedTemplateId(templateId);

      if (template.schema && Array.isArray(template.schema) && template.schema.length > 0) {
        // Template mode: use dynamic form renderer
        setTemplateSchema(template.schema as TemplateFieldControl[]);
        setTemplateValues({});
        setSoapData(prev => ({ ...prev, templateUsed: template.name }));
        enqueueSnackbar(`Template "${template.name}" loaded — fill in the structured form below`, { variant: 'success' });
      } else {
        // No fields of its own (General Exam): the standard four sections. Its prompt lists are never shown
        // here, so they are not copied in; copying replaced what the vet had typed and the Plan rows.
        setTemplateSchema(null);
        setSoapData((prev) => ({ ...prev, templateUsed: template.name }));
        enqueueSnackbar(`Template "${template.name}" applied`, { variant: 'success' });
      }
      setIsDirty(true);
    } catch (error) {
      enqueueSnackbar('Failed to load template', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Map template field values to SOAP sections before saving.
  // Also ensures aiSummary exists for each section so data is visible in non-template UI,
  // completed note view, and mobile app.
  const mergeTemplateValuesToSoapData = () => {
    if (!templateSchema) return soapData;
    const flatFields = flattenSchema(templateSchema);

    // Build child→group map for nested value lookup
    const childToGroup: Record<string, string> = {};
    templateSchema.forEach(f => {
      if (f.type === 'group' && f.children) {
        f.children.forEach((child: TemplateFieldControl) => {
          childToGroup[child.key] = f.key;
        });
      }
    });

    // These SOAP keys hold structured data (arrays/objects) that template text fields must not overwrite
    const reservedKeys = new Set([
      'treatments', 'medicationsPrescribed', 'proceduresPrescribed', 'diagnosticsOrdered',
      'vitals', 'physicalExam', 'lamenessEval',
      'differentials', 'prompts', 'responses',
    ]);

    const sections: Record<string, Record<string, any>> = {
      subjective: {},
      objective: {},
      assessment: {},
      plan: {},
    };
    flatFields.forEach(field => {
      // Don't let template textarea/text fields overwrite structured SOAP arrays/objects
      if (reservedKeys.has(field.key) && (field.type === 'textarea' || field.type === 'text')) {
        // Store as a template-specific key so the data is still saved
        const groupKey = childToGroup[field.key];
        const val = groupKey
          ? (templateValues[groupKey] as Record<string, any>)?.[field.key]
          : templateValues[field.key];
        if (val !== undefined && val !== '') {
          sections[field.section][`tpl_${field.key}`] = val;
        }
        return;
      }
      const groupKey = childToGroup[field.key];
      const val = groupKey
        ? (templateValues[groupKey] as Record<string, any>)?.[field.key]
        : templateValues[field.key];
      if (val !== undefined && val !== '') {
        sections[field.section][field.key] = val;
      }
    });

    // Preserve existing aiSummary from voice AI, or generate from template values as fallback
    const mergedSubjective = { ...soapData.subjective, ...sections.subjective };
    const mergedObjective = { ...soapData.objective, ...sections.objective };
    const mergedAssessment = { ...soapData.assessment, ...sections.assessment };
    const mergedPlan = { ...soapData.plan, ...sections.plan };

    if (!mergedSubjective.aiSummary) {
      const summary = buildSummaryFromTemplateValues('subjective', templateSchema, templateValues);
      if (summary) mergedSubjective.aiSummary = summary;
    }
    if (!mergedObjective.aiSummary) {
      const summary = buildSummaryFromTemplateValues('objective', templateSchema, templateValues);
      if (summary) mergedObjective.aiSummary = summary;
    }
    if (!mergedAssessment.aiSummary) {
      const summary = buildSummaryFromTemplateValues('assessment', templateSchema, templateValues);
      if (summary) mergedAssessment.aiSummary = summary;
    }
    // With the Plan block on screen, planNotes is the vet's own box: a summary written there reads as theirs.
    const showsPlanBlock = templateSchema.some((f) => f.type === 'treatment_plan');
    if (!showsPlanBlock && !mergedPlan.planNotes && !(mergedPlan as any).followUpInstructions) {
      const summary = buildSummaryFromTemplateValues('plan', templateSchema, templateValues);
      if (summary) mergedPlan.planNotes = summary;
    }

    return {
      ...soapData,
      subjective: mergedSubjective,
      objective: mergedObjective,
      assessment: mergedAssessment,
      plan: mergedPlan,
    };
  };

  // Handle AI extraction results from a file_drop field OR the universal
  // Attachments section. Matches against the FLATTENED schema so group-child
  // fields are recognized (top-level-only matching silently dropped them).
  const handleAIExtract = (fieldKey: string, attachmentId: string, extracted: any) => {
    if (!extracted?.fields) return;
    // Template mode maps to the template's fields; no-template mode maps to the
    // four generic SOAP sections — so AI document-fill works on every note.
    const activeSchema = templateSchema || GENERIC_SOAP_SCHEMA;
    const flatFields = flattenSchema(activeSchema);
    const suggestions: AIFieldSuggestion[] = [];
    Object.entries(extracted.fields as Record<string, any>).forEach(([key, data]: [string, any]) => {
      const schemaField = flatFields.find(f => f.key === key);
      if (schemaField && data?.value !== undefined) {
        suggestions.push({
          fieldKey: key,
          fieldLabel: schemaField.label,
          value: data.value,
          confidence: data.confidence || 0.5,
          source: data.source,
        });
      }
    });
    if (suggestions.length) {
      setAiSuggestions(suggestions);
      setAiDocMeta({
        name: extracted.documentType,
        summary: extracted.summary,
        documentType: extracted.documentType,
        warnings: extracted.warnings,
      });
    } else {
      enqueueSnackbar('No matching fields found in document', { variant: 'info' });
    }
  };

  const handleTemplateFieldChange = useCallback((key: string, value: any) => {
    setTemplateValues(tv => ({ ...tv, [key]: value }));
    setIsDirty(true);
  }, []);

  // Handle section updates
  const updateSection = (section: keyof CreateSoapNoteDto, data: any) => {
    setSoapData((prev) => ({
      ...prev,
      [section]: data,
    }));
    setIsDirty(true);
  };

  // Handle voice-generated SOAP data
  const handleVoiceSoapGenerated = (data: {
    subjective: SoapSubjective;
    objective: SoapObjective;
    assessment: SoapAssessment;
    plan: SoapPlan;
    originalTranscript?: string;
    templateValues?: Record<string, any>;
  }) => {
    // Always store in soapData (used when saving, and for legacy/no-template mode)
    setSoapData((prev) => {
      const updated = {
        ...prev,
        subjective: { ...prev.subjective, ...data.subjective },
        objective: { ...prev.objective, ...data.objective },
        assessment: { ...prev.assessment, ...data.assessment },
        plan: { ...prev.plan, ...data.plan },
        originalTranscript: data.originalTranscript,
      } as CreateSoapNoteDto;

      return updated;
    });

    // If in template mode, fill template fields from AI response
    if (templateSchema) {
      const flatFields = flattenSchema(templateSchema);

      // Build a map of child key → parent group key for nesting values correctly
      // TemplateFormRenderer stores group children as: values[groupKey] = { childKey: value }
      const childToGroup: Record<string, string> = {};
      templateSchema.forEach(f => {
        if (f.type === 'group' && f.children) {
          f.children.forEach((child: TemplateFieldControl) => {
            childToGroup[child.key] = f.key;
          });
        }
      });

      // Helper to set a value respecting group nesting
      const setFieldValue = (newValues: Record<string, any>, fieldKey: string, value: any) => {
        const groupKey = childToGroup[fieldKey];
        if (groupKey) {
          // Nest under parent group key
          newValues[groupKey] = { ...(newValues[groupKey] || {}), [fieldKey]: value };
        } else {
          newValues[fieldKey] = value;
        }
      };

      // If AI returned templateValues (it knows the schema), use them directly
      if (data.templateValues && Object.keys(data.templateValues).length > 0) {
        setTemplateValues((prev) => {
          const newValues = { ...prev };
          flatFields.forEach((field) => {
            // Don't overwrite values already filled by the user
            const groupKey = childToGroup[field.key];
            if (groupKey) {
              const existing = (newValues[groupKey] as Record<string, any>)?.[field.key];
              if (existing !== undefined && existing !== '') return;
            } else {
              if (newValues[field.key] !== undefined && newValues[field.key] !== '') return;
            }
            if (data.templateValues![field.key] !== undefined) {
              setFieldValue(newValues, field.key, data.templateValues![field.key]);
            }
          });
          return newValues;
        });
      } else {
        // Fallback: pattern-match voice AI structured data to template fields
        setTemplateValues((prev) => {
          const newValues = { ...prev };
          const filledTextSections = new Set<string>();

          flatFields.forEach((field) => {
            const groupKey = childToGroup[field.key];
            if (groupKey) {
              const existing = (newValues[groupKey] as Record<string, any>)?.[field.key];
              if (existing !== undefined && existing !== '') return;
            } else {
              if (newValues[field.key] !== undefined && newValues[field.key] !== '') return;
            }

            const sectionData = data[field.section as keyof typeof data];
            if (!sectionData) return;

            const matched = matchVoiceDataToField(field, sectionData, data);
            if (matched !== undefined && matched !== '') {
              if ((field.type === 'textarea' || field.type === 'text') && matched === (sectionData as any)?.aiSummary) {
                if (filledTextSections.has(field.section)) return;
                filledTextSections.add(field.section);
              }
              const val = field.type === 'number' ? Number(matched) || matched : matched;
              setFieldValue(newValues, field.key, val);
            }
          });

          return newValues;
        });
      }
    }

    setIsDirty(true);
    enqueueSnackbar('Voice transcript converted to SOAP note. Please review and edit as needed.', {
      variant: 'success'
    });
  };

  // Offline-aware draft upsert: create a new note (temp id) or update an
  // existing/temp one, so an offline draft is queued + optimistically shown.
  // Online it is the same /soap-notes/upsert call as before (engine passthrough).
  const persistDraft = (data: CreateSoapNoteDto) => {
    const payload = { ...data, status: SoapNoteStatus.DRAFT };
    return payload.id
      ? executeUpdate({
          entityType: 'soapNote',
          table: 'soapNotes',
          entityId: payload.id,
          payload,
          networkFn: () => soapNotesService.upsert(payload),
        })
      : executeWrite({
          entityType: 'soapNote',
          table: 'soapNotes',
          payload,
          networkFn: () => soapNotesService.upsert(payload),
          // Optimistic-row-only author (never the op payload) — see hook note.
          author: authUser
            ? {
                providerId: authUser.uid,
                provider: {
                  id: authUser.uid,
                  name: authUser.displayName || authUser.email?.split('@')[0] || 'You',
                },
              }
            : undefined,
        });
  };

  // Handle save as draft
  const handleSaveDraft = async () => {
    setLoading(true);
    try {
      const mergedData = mergeTemplateValuesToSoapData();
      const savedNote: any = await persistDraft(mergedData);
      setSoapData({ ...soapData, id: savedNote.id });
      setIsDirty(false);
      setLastSaved(new Date());
      trackFeatureUsed('soap_note_saved_draft');
      enqueueSnackbar('SOAP note saved as draft', { variant: 'success' });
      onSave?.(savedNote);
    } catch (error) {
      enqueueSnackbar(
        error instanceof OfflineWriteBlockedError ? error.message : 'Failed to save SOAP note',
        { variant: 'error' },
      );
    } finally {
      setLoading(false);
    }
  };

  // Resolve this note's id, auto-saving a draft the FIRST time a file is
  // uploaded so the user never has to "save first". Returns the id (or undefined
  // on failure / if abandoned). Deliberately does NOT call onSave (which would
  // close the dialog). The dedupe + epoch-discard concurrency invariants live in
  // draftCoordinator (unit-tested); this just supplies persist + the side effects.
  const ensureNoteId = async (): Promise<string | undefined> => {
    if (soapData.id) return soapData.id;
    try {
      const id = await draftCoordinator.ensure(
        async () => {
          setLoading(true);
          try {
            const mergedData = mergeTemplateValuesToSoapData();
            const savedNote: any = await persistDraft(mergedData);
            return savedNote.id as string;
          } finally {
            setLoading(false);
          }
        },
        () => openEpochRef.current,
      );
      // id is undefined when the coordinator discarded it (note changed mid-flight).
      if (id) {
        skipNextLoadRef.current = id;
        setSoapData((prev) => ({ ...prev, id }));
        setIsDirty(false);
        setLastSaved(new Date());
      }
      return id;
    } catch (error) {
      enqueueSnackbar(
        error instanceof OfflineWriteBlockedError ? error.message : 'Failed to save note',
        { variant: 'error' },
      );
      return undefined;
    }
  };

  // Handle complete
  const handleComplete = async () => {
    const mergedData = mergeTemplateValuesToSoapData();
    // Validate required fields
    const hasContent =
      (mergedData.subjective && Object.keys(mergedData.subjective).length > 0) ||
      (mergedData.objective && Object.keys(mergedData.objective).length > 0) ||
      (mergedData.assessment && Object.keys(mergedData.assessment).length > 0) ||
      (mergedData.plan && Object.keys(mergedData.plan).length > 0);

    if (!hasContent) {
      enqueueSnackbar('At least one SOAP section must have content', { variant: 'warning' });
      return;
    }

    // Offline: "Complete" is an online-only action (custom /complete endpoint runs
    // a server transaction that spawns medications/procedures and applies DEA
    // Schedule-II controlled-substance clamping — never route through executeWrite,
    // queuing it risks duplicate controlled-substance records + bypasses the C-II
    // gate). The Complete button is disabled offline; this is the belt-and-suspenders
    // early-return. We still save the work as a DRAFT (allowed offline path) so the
    // vet's PHI is persisted, and tell them to complete on reconnect.
    if (!skipWhenOffline()) {
      setLoading(true);
      try {
        const savedNote: any = await persistDraft(mergedData);
        setSoapData({ ...soapData, id: savedNote.id });
        setIsDirty(false);
        setLastSaved(new Date());
        enqueueSnackbar('Offline — saved as a draft. Complete this note when you reconnect.', {
          variant: 'info',
        });
        onSave?.(savedNote);
      } catch (error) {
        enqueueSnackbar(
          error instanceof OfflineWriteBlockedError ? error.message : 'Failed to save offline',
          { variant: 'error' },
        );
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    try {
      let noteId = mergedData.id;

      // Save first if it's a new note, and keep its id: Back on a prompt below returns here, and a second
      // Complete must finish this note, not create another.
      if (!noteId) {
        const savedNote = await soapNotesService.create(mergedData);
        noteId = savedNote.id;
        setSoapData((prev) => ({ ...prev, id: savedNote.id }));
      } else if (isDirty) {
        // `/complete` carries no date, so a date changed since the last autosave is saved first. Online
        // only, like the create above: a queued save would land after the note is locked.
        await soapNotesService.upsert({ ...mergedData, status: SoapNoteStatus.DRAFT });
        setIsDirty(false);
      }

      // Vaccines first (spec §2): which of them become vaccination records decides the medication list.
      if (planVaccines.length > 0) {
        setVaccinePrompt({ completion: { noteId, plan: mergedData.plan || null } });
        setLoading(false);
        return;
      }
      await continueCompletion(noteId, mergedData.plan || null, settledVaccines());
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to prepare SOAP note completion', {
        variant: 'error'
      });
      setLoading(false);
    }
  };

  // The medication and procedure confirmations, then completion (use the merged plan, which includes template values).
  const continueCompletion = async (noteId: string, plan: SoapPlan | null, recorded: Set<string>) => {
    setLoading(true);
    try {
      const medications = (Array.isArray(plan?.medicationsPrescribed) ? plan!.medicationsPrescribed! : []).filter(
        (m) => !isRecordedVaccineRow(m, recorded),
      );
      const procedures = Array.isArray(plan?.proceduresPrescribed) ? plan!.proceduresPrescribed! : [];

      setPendingMergedPlan(plan);
      setCompletionVaccines(recorded);

      if (medications.length > 0) {
        // Show medication confirmation dialog first
        setPendingNoteId(noteId);
        setShowMedicationDialog(true);
        setLoading(false);
      } else if (procedures.length > 0) {
        // No medications but have procedures, show procedure dialog
        setPendingNoteId(noteId);
        setPendingMedications([]);
        setShowProcedureDialog(true);
        setLoading(false);
      } else {
        // No medications or procedures, complete directly
        await completeWithMedicationsAndProcedures(noteId, [], []);
      }
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to complete SOAP note', { variant: 'error' });
      setLoading(false);
    }
  };

  // Handle medication confirmation
  const handleMedicationConfirm = async (confirmedMedications: MedicationToAdd[]) => {
    if (!pendingNoteId) return;

    setShowMedicationDialog(false);
    setPendingMedications(confirmedMedications);

    // Check if there are procedures to confirm next (use merged plan to include template data)
    const procedures = Array.isArray(pendingMergedPlan?.proceduresPrescribed) ? pendingMergedPlan!.proceduresPrescribed! : [];
    if (procedures.length > 0) {
      // Show procedure confirmation dialog
      setShowProcedureDialog(true);
    } else {
      // No procedures, complete with just medications
      setLoading(true);
      try {
        await completeWithMedicationsAndProcedures(pendingNoteId, confirmedMedications, []);
      } catch (error: any) {
        enqueueSnackbar(error.response?.data?.message || 'Failed to complete SOAP note', { 
          variant: 'error' 
        });
      } finally {
        setLoading(false);
        setPendingNoteId(null);
        setPendingMedications([]);
        setPendingMergedPlan(null);
      }
    }
  };

  // Handle medication dialog cancel
  const handleMedicationCancel = () => {
    setShowMedicationDialog(false);
    setPendingNoteId(null);
    setPendingMedications([]);
    setPendingMergedPlan(null);
    setLoading(false);
  };

  // Handle procedure confirmation
  const handleProcedureConfirm = async (confirmedProcedures: ProcedureToAdd[]) => {
    if (!pendingNoteId) return;

    setShowProcedureDialog(false);
    setLoading(true);

    try {
      await completeWithMedicationsAndProcedures(pendingNoteId, pendingMedications, confirmedProcedures);
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to complete SOAP note', { 
        variant: 'error' 
      });
    } finally {
      setLoading(false);
      setPendingNoteId(null);
      setPendingMedications([]);
      setPendingMergedPlan(null);
    }
  };

  // Handle procedure dialog cancel
  const handleProcedureCancel = () => {
    setShowProcedureDialog(false);
    setPendingNoteId(null);
    setPendingMedications([]);
    setPendingMergedPlan(null);
    setLoading(false);
  };

  const handleVaccinationsRecorded = async (rows: any[]) => {
    for (const row of rows) {
      const data = toCreateVaccinationDto(row, horseId);
      await executeWrite({
        entityType: 'vaccination',
        table: 'vaccinations',
        payload: data,
        networkFn: () => VaccinationService.create(data),
      });
    }
    const saved = new Set(
      detectVaccines(rows.map((r) => ({ name: r.vaccineName || r.name })), vaccineCatalog).map((d) => d.catalogItem.name),
    );
    savedFromPromptRef.current = saved;
    setRecordedVaccines((prev) => new Set(Array.from(prev).concat(Array.from(saved))));
    queryClient.invalidateQueries(['vaccinations', horseId]);
    queryClient.invalidateQueries(['horse-records', horseId]);
    enqueueSnackbar(rows.length === 1 ? 'Vaccination recorded' : `${rows.length} vaccinations recorded`, { variant: 'success' });
  };

  // The dialog closes after a save (carry on) or on Back / Escape (abandon the completion; the note stays open).
  const handleVaccinationPromptClosed = () => {
    const completion = vaccinePrompt?.completion;
    const saved = savedFromPromptRef.current;
    savedFromPromptRef.current = null;
    setVaccinePrompt(null);
    if (!saved) setDoseDecisions({});
    if (completion && saved) {
      void continueCompletion(completion.noteId, completion.plan, settledVaccines(saved));
    }
  };

  // "Skip and complete": the plan's vaccines stay what they were (a medication line, if one).
  const handleVaccinationPromptSkipped = () => {
    const completion = vaccinePrompt?.completion;
    setVaccinePrompt(null);
    if (completion) void continueCompletion(completion.noteId, completion.plan, settledVaccines());
  };

  // An answer to "is it that dose?". Once every plan vaccine is settled there is nothing to record.
  const answerExistingDose = (name: string, answer: 'same' | 'new') => {
    const next = { ...doseDecisions, [name]: answer };
    setDoseDecisions(next);
    if (splitPlanVaccines(planVaccineNames, recordedVaccines, next, (n) => dosesOnChart(n).length > 0).pending.length > 0) return;
    const completion = vaccinePrompt?.completion;
    setVaccinePrompt(null);
    if (completion) void continueCompletion(completion.noteId, completion.plan, settledVaccines([name]));
    else enqueueSnackbar('Already on the chart. Nothing new recorded.', { variant: 'info' });
  };

  // Complete note with medications and procedures
  const completeWithMedicationsAndProcedures = async (
    noteId: string, 
    medications: MedicationToAdd[], 
    procedures: ProcedureToAdd[]
  ) => {
    const mergedData = mergeTemplateValuesToSoapData();
    const completedNote = await soapNotesService.complete(noteId, {
      subjective: mergedData.subjective,
      objective: mergedData.objective,
      assessment: mergedData.assessment,
      plan: mergedData.plan,
      medications: medications.length > 0 ? medications : undefined,
      procedures: procedures.length > 0 ? procedures : undefined,
    });

    // The server created the procedures in the completion transaction (INV-118).
    if (procedures.length > 0) {
      queryClient.invalidateQueries(['procedures', horseId]);
      queryClient.invalidateQueries(['horse-records', horseId]);
    }

    enqueueSnackbar('SOAP note completed successfully', { variant: 'success' });
    if (procedures.length > 0) {
      enqueueSnackbar(`${procedures.length} procedure(s) created. Complete details in the Procedures page.`, { 
        variant: 'info',
        autoHideDuration: 5000,
      });
    }
    onSave?.(completedNote);
    onClose();
  };

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const isCompleted = existingNote?.status === SoapNoteStatus.COMPLETED;

  // One Plan block for every layout, a template's Full Plan field included.
  const planSection = (
    <PlanSection
      data={soapData.plan || {}}
      onChange={(data) => updateSection('plan', data)}
      disabled={isCompleted}
      detectedVaccines={planVaccines}
      vaccineEntryNames={vaccineEntryNames}
      onRecordVaccinations={() => setVaccinePrompt({})}
      treatmentOptions={treatmentOptions}
    />
  );

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <Box>
              <Typography variant="h6">
                SOAP Note for {horseName || 'Patient'}
              </Typography>
              {horseBreed && (
                <Typography variant="caption" color="text.secondary">
                  {horseBreed}
                </Typography>
              )}
            </Box>
            {isCompleted && (
              <Chip
                label="Completed"
                color="success"
                size="small"
                icon={<CheckCircleIcon />}
              />
            )}
            {!isCompleted && soapData.id && (
              <Chip
                label="Draft"
                color="warning"
                size="small"
              />
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {existingNote?.originalTranscript && (
              <Tooltip title={showTranscript ? 'Hide original transcript' : 'Show original transcript'}>
                <IconButton 
                  size="small"
                  onClick={() => setShowTranscript((p) => !p)}
                  sx={{ 
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main' }
                  }}
                >
                  <ChatIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {!isCompleted && templates.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Template</InputLabel>
                <Select
                  value={selectedTemplateId}
                  onChange={(e) => handleTemplateSelect(e.target.value)}
                  label="Template"
                  startAdornment={<TemplateIcon sx={{ mr: 1 }} />}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {[...templates]
                    .sort((a, b) => Number(isDefaultLayout(b)) - Number(isDefaultLayout(a)))
                    .map((template) => (
                      <MenuItem key={template.id} value={template.id}>
                        {template.name}
                        {isDefaultLayout(template) && (
                          <Chip label="Default" size="small" sx={{ ml: 1 }} />
                        )}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}
            {saving && (
              <Tooltip title="Autosaving...">
                <CircularProgress size={20} />
              </Tooltip>
            )}
            {lastSaved && !saving && (
              <Typography variant="caption" color="text.secondary">
                Last saved: {format(lastSaved, 'HH:mm:ss')}
              </Typography>
            )}
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      {/* Global style override to darken disabled inputs for completed note readability */}
      <GlobalStyles styles={{ 
        '.MuiInputBase-input.Mui-disabled': { 
          color: 'rgba(0,0,0,0.87) !important',
          WebkitTextFillColor: 'rgba(0,0,0,0.87) !important'
        },
        '.MuiInputBase-root.Mui-disabled .MuiInputBase-input': {
          color: 'rgba(0,0,0,0.87) !important',
          WebkitTextFillColor: 'rgba(0,0,0,0.87) !important'
        },
        '.MuiInputLabel-root.Mui-disabled': {
          color: 'rgba(0,0,0,0.6) !important'
        },
        '.MuiFormControlLabel-label.Mui-disabled': {
          color: 'rgba(0,0,0,0.87) !important'
        },
        '.MuiCheckbox-root.Mui-disabled': {
          color: 'rgba(0,0,0,0.6) !important'
        },
        '.MuiSwitch-root .Mui-disabled': {
          color: 'rgba(0,0,0,0.6) !important'
        },
        '.MuiSelect-select.Mui-disabled': {
          color: 'rgba(0,0,0,0.87) !important',
          WebkitTextFillColor: 'rgba(0,0,0,0.87) !important'
        }
      }} />

      <DialogContent dividers>
        {/* An instant (DATE-KINDS `SoapNote.date`), as on iPhone; locked with the rest of a completed note. */}
        <Box sx={{ mb: 2, maxWidth: { sm: 280 } }}>
          <DateTimePicker
            label="Date & time"
            value={new Date(soapData.date)}
            onChange={(picked) => {
              if (picked && !isNaN(picked.getTime())) updateSection('date', picked.toISOString());
            }}
            disabled={isCompleted}
            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
          />
        </Box>
        {existingNote?.originalTranscript && (
          <Collapse in={showTranscript} timeout="auto" unmountOnExit>
            <Box sx={{ 
              mb: 3, 
              p: 2, 
              bgcolor: 'grey.50', 
              borderRadius: 1, 
              border: '1px solid', 
              borderColor: 'grey.200'
            }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Original Voice Transcript
              </Typography>
              <Typography variant="body2" sx={{ 
                fontStyle: 'italic', 
                whiteSpace: 'pre-wrap', 
                maxHeight: 150, 
                overflow: 'auto'
              }}>
                {existingNote.originalTranscript}
              </Typography>
            </Box>
          </Collapse>
        )}
        {/* Voice-to-SOAP Recorder - placed at the top */}
        {!isCompleted && (
          <Box sx={{ mb: 2 }}>
            {isOffline ? (
              // Voice offline is a deferred feature — the browser speech API and
              // the generate call are both cloud-backed, so show a notice instead
              // of an active mic that can only fail.
              <OfflineNotice sx={{ mb: 0 }}>
                Voice-to-SOAP needs a connection — type your note or save a draft
                and dictate when you're back online.
              </OfflineNotice>
            ) : (
              <VoiceSoapRecorder
                horseId={horseId}
                horseName={horseName}
                horseBreed={horseBreed}
                horseAge={horseAge}
                onSoapGenerated={handleVoiceSoapGenerated}
                isSubscribed={isSubscribed}
                subscriptionTier={subscriptionTier || undefined}
                templateSchema={aiTemplateSchema || undefined}
              />
            )}
          </Box>
        )}

        {/* AI Review Panel — shown when AI extraction results are available */}
        {aiSuggestions && aiSuggestions.length > 0 && (
          <Box mb={2}>
            <AIReviewPanel
              suggestions={aiSuggestions}
              documentName={aiDocMeta?.name}
              summary={aiDocMeta?.summary}
              documentType={aiDocMeta?.documentType}
              warnings={aiDocMeta?.warnings}
              onAccept={(fieldKey, value) => {
                if (templateSchema) {
                  setTemplateValues(tv => {
                    // Nest under the parent group key if this is a group child,
                    // mirroring how TemplateFormRenderer stores group values.
                    let groupKey: string | undefined;
                    templateSchema.forEach((f: any) => {
                      if (f.type === 'group' && Array.isArray(f.children)
                        && f.children.some((c: any) => c.key === fieldKey)) groupKey = f.key;
                    });
                    if (groupKey) {
                      return { ...tv, [groupKey]: { ...((tv[groupKey] as any) || {}), [fieldKey]: value } };
                    }
                    return { ...tv, [fieldKey]: value };
                  });
                } else {
                  // No template: fieldKey is one of the four SOAP sections —
                  // fill that section's free-text. The sections are NOT
                  // symmetric: Subjective/Objective/Assessment bind to
                  // `aiSummary`, but Plan binds to `planNotes`.
                  const section = GENERIC_SOAP_SCHEMA.find(f => f.key === fieldKey)?.section as
                    keyof CreateSoapNoteDto | undefined;
                  if (section) {
                    const targetKey = section === 'plan' ? 'planNotes' : 'aiSummary';
                    updateSection(section, { ...((soapData as any)[section] || {}), [targetKey]: value });
                  }
                }
                setIsDirty(true);
              }}
              onAcceptAll={() => {
                setAiSuggestions(null);
                setAiDocMeta(null);
              }}
              onDismiss={() => {
                setAiSuggestions(null);
                setAiDocMeta(null);
              }}
            />
          </Box>
        )}

        {/* Attachments — the SINGLE place files live for a note, surfaced at the
            TOP so it's obvious on create. Shows every file on the note (template
            file_drop fields are suppressed in the body via hideFileDrop to avoid
            a duplicate). Tap a document's AI sparkle to read it into the note —
            into template fields when a template is active, else into the four
            SOAP sections. A brand-new note has no id yet, so the first upload
            auto-saves a draft just-in-time (FileDropZone's ensureParentId). */}
        <Paper
          variant="outlined"
          sx={{ p: 1.5, mb: 2, borderRadius: 2, borderColor: 'divider', bgcolor: 'grey.50' }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
            <AttachFileIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" fontWeight={700}>
              Attachments
            </Typography>
            {noteAttachments.length > 0 && (
              <Chip label={noteAttachments.length} size="small" color="primary" sx={{ height: 20, fontWeight: 700 }} />
            )}
            {!isCompleted && noteAttachments.length > 0 && (
              <Box display="flex" alignItems="center" gap={0.5} sx={{ ml: 'auto', color: 'text.secondary' }}>
                <AutoAwesomeIcon sx={{ fontSize: 15 }} />
                <Typography variant="caption">Tap the sparkle on a document to fill this note with AI</Typography>
              </Box>
            )}
          </Box>
          <FileDropZone
            horseId={horseId}
            parentType="soap_note"
            parentId={soapData.id}
            // Brand-new note has no id yet — auto-save a draft on first upload
            // instead of forcing the user to save first.
            ensureParentId={!isCompleted ? ensureNoteId : undefined}
            fieldKey={NOTE_FILES_FIELD_KEY}
            category="soap_attachment"
            multiple
            value={noteAttachments}
            onChange={(next) => {
              setNoteAttachments(next);
              // Keep the patient chart's Files badge/list fresh after an
              // upload/remove made from inside the note.
              queryClient.invalidateQueries(patientFilesQueryKey(horseId));
            }}
            // AI "read this document into the note" — works on every note:
            // template mode fills the template fields, no-template mode fills
            // the four SOAP sections (via GENERIC_SOAP_SCHEMA).
            onAIExtract={!isCompleted
              ? (attachmentId, extracted) => handleAIExtract('', attachmentId, extracted)
              : undefined}
            templateSchema={aiTemplateSchema || GENERIC_SOAP_SCHEMA}
            readOnly={isCompleted}
          />
        </Paper>

        {templateSchema ? (
          // Template mode: dynamic form renderer
          <Box sx={{ minHeight: '60vh' }}>
            <TemplateFormRenderer
              schema={templateSchema}
              values={templateValues}
              onChange={handleTemplateFieldChange}
              horseId={horseId}
              soapNoteId={soapData.id}
              onAIExtract={handleAIExtract}
              readOnly={isCompleted}
              hideFileDrop
              treatmentPlan={planSection}
            />
          </Box>
        ) : isMobile ? (
          // Mobile: Tabbed interface
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
              <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
                <Tab label="Subjective" />
                <Tab label="Objective" />
                <Tab label="Assessment" />
                <Tab label="Plan" />
              </Tabs>
            </Box>
            <TabPanel value={activeTab} index={0}>
              <SubjectiveSection
                data={soapData.subjective || {}}
                onChange={(data) => updateSection('subjective', data)}
                disabled={isCompleted}
              />
            </TabPanel>
            <TabPanel value={activeTab} index={1}>
              <ObjectiveSection
                data={soapData.objective || {}}
                onChange={(data) => updateSection('objective', data)}
                disabled={isCompleted}
              />
            </TabPanel>
            <TabPanel value={activeTab} index={2}>
              <AssessmentSection
                data={soapData.assessment || {}}
                onChange={(data) => updateSection('assessment', data)}
                disabled={isCompleted}
              />
            </TabPanel>
            <TabPanel value={activeTab} index={3}>
              {planSection}
            </TabPanel>
          </>
        ) : (
          // Desktop: Grid layout with all sections visible
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 3,
              minHeight: '60vh',
            }}
          >
            <Box>
              <Typography variant="h6" gutterBottom color="primary">
                Subjective
              </Typography>
              <SubjectiveSection
                data={soapData.subjective || {}}
                onChange={(data) => updateSection('subjective', data)}
                disabled={isCompleted}
              />
            </Box>
            <Box>
              <Typography variant="h6" gutterBottom color="primary">
                Objective
              </Typography>
              <ObjectiveSection
                data={soapData.objective || {}}
                onChange={(data) => updateSection('objective', data)}
                disabled={isCompleted}
              />
            </Box>
            <Box>
              <Typography variant="h6" gutterBottom color="primary">
                Assessment
              </Typography>
              <AssessmentSection
                data={soapData.assessment || {}}
                onChange={(data) => updateSection('assessment', data)}
                disabled={isCompleted}
              />
            </Box>
            <Box>
              <Typography variant="h6" gutterBottom color="primary">
                Plan
              </Typography>
              {planSection}
            </Box>
          </Box>
        )}

      </DialogContent>

      {!isCompleted && (
        <DialogActions sx={{ p: 2, position: isMobile ? 'sticky' : 'relative', bottom: 0, bgcolor: 'background.paper' }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSaveDraft}
            disabled={loading || !isDirty}
            startIcon={<SaveIcon />}
          >
            Save Draft
          </Button>
          <Tooltip
            title={skipWhenOffline() ? '' : 'Finalize when back online — completion runs a server transaction (medications, procedures, controlled-substance rules) that cannot be queued offline. Your work is saved as a draft.'}
          >
            {/* span wrapper so the Tooltip still shows while the Button is disabled */}
            <span>
              <Button
                onClick={handleComplete}
                disabled={loading || !skipWhenOffline()}
                variant="contained"
                color="primary"
                startIcon={<CheckCircleIcon />}
              >
                Complete
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      )}
    </Dialog>

      {/* Medication Confirmation Dialog */}
      <MedicationConfirmationDialog
        open={showMedicationDialog}
        medications={(soapData.plan?.medicationsPrescribed || []).filter((m) => !isRecordedVaccineRow(m, completionVaccines))}
        noteDate={soapData.date}
        onConfirm={handleMedicationConfirm}
        onCancel={handleMedicationCancel}
      />

      {/* Vaccines in the plan, recorded with their lot and booster (spec §2). */}
      <ExistingVaccinationDialog
        open={!!vaccinePrompt && chartSettled && toAsk.length > 0}
        vaccineName={toAsk[0] ?? ''}
        horseName={horseName || ''}
        doses={toAsk[0] ? dosesOnChart(toAsk[0]) : []}
        onSame={() => answerExistingDose(toAsk[0], 'same')}
        onNew={() => answerExistingDose(toAsk[0], 'new')}
        onBack={() => {
          setVaccinePrompt(null);
          setDoseDecisions({});
        }}
      />
      <VaccinationDialog
        open={!!vaccinePrompt && chartSettled && toAsk.length === 0}
        title="Record vaccinations"
        intro={
          vaccinePrompt?.completion
            ? 'These vaccines are in the plan. Record them so the lot and booster are tracked, or skip to complete the note without them.'
            : undefined
        }
        cancelLabel={vaccinePrompt?.completion ? 'Back' : 'Cancel'}
        onSkip={vaccinePrompt?.completion ? handleVaccinationPromptSkipped : undefined}
        initialNames={vaccinesToRecord}
        initialDate={soapData.date ? new Date(soapData.date) : undefined}
        onClose={handleVaccinationPromptClosed}
        onSave={handleVaccinationsRecorded}
        currentUserName={authUser?.displayName || authUser?.email?.split('@')[0] || ''}
        catalogItems={vaccineCatalog.filter((i) => i.isActive)}
      />

      {/* Procedure Confirmation Dialog */}
      <ProcedureConfirmationDialog
        open={showProcedureDialog}
        procedures={soapData.plan?.proceduresPrescribed || []}
        noteDate={soapData.date}
        onConfirm={handleProcedureConfirm}
        onCancel={handleProcedureCancel}
      />
    </>
  );
};

export default SoapNoteDialog;
